import React from 'react';
import { Card, OverlayTrigger, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './bloodwork-anemia-status.css';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import { adrenalGauge, AdrenalGauge } from '../../models/StressAdrenalAnalysis';
import { LabsmartsGuage } from '../charts/LabsmartsGauge';

// Up/down arrows for the marker row — same assets the Glucose Insights panel uses.
const arrow_up = require('../../assets/imgs/long-arrow-up-8x13.png');
const arrow_down = require('../../assets/imgs/long-arrow-down-8x12.png');

const arrowStyle: React.CSSProperties = {
  height: '11px',
  margin: '0 3px',
  verticalAlign: 'middle',
  position: 'relative',
  top: '-0.5px',
};

type Props = { bloodworkAnalysis: BloodworkAnalysis };

// Gauge-label prefix per direction. The likelihood word + percent are appended
// by the gauge, so this reads e.g. "Adrenal Insufficiency is Likely (80%)".
const CONDITION_LABEL: Record<string, string> = {
  insufficiency: 'Adrenal Insufficiency',
  excess: 'Adrenal Excess',
  balanced: 'Adrenal Dysfunction',
};

export class StressAdrenalRegulationInsights extends React.Component<Props, {}> {
  // Explains the gauge — mirrors the Thyroid Insights header info-circle Popover
  // (same icon, placement, and styling).
  gaugeInfoPopover = () => (
    <Popover id="stress-adrenal-gauge-info" style={{ maxWidth: '340px' }}>
      <Popover.Title as="div" style={{ fontSize: '13px', fontWeight: 700, backgroundColor: '#fff', borderBottom: 'none' }}>
        About this gauge
      </Popover.Title>
      <Popover.Content>
        <div style={{ textAlign: 'left', fontSize: '12px', color: '#555', lineHeight: 1.45 }}>
          <div style={{ marginBottom: '8px' }}>
            This gauge reads the adrenal axis on the same 0–100% scale as the other panels. The{' '}
            <strong>sodium / potassium (Na/K) ratio</strong> is the primary marker and sets the direction:
          </div>
          <ul style={{ paddingLeft: '16px', marginBottom: '8px' }}>
            <li><strong>Below optimal</strong> — points toward adrenal insufficiency.</li>
            <li><strong>Above optimal</strong> — points toward adrenal excess.</li>
            <li><strong>Within optimal</strong> — adrenal dysfunction is less likely.</li>
          </ul>
          <div style={{ marginBottom: 0 }}>
            <strong>Cortisol</strong> (AM/PM), <strong>DHEA-S</strong> and <strong>Pregnenolone</strong> refine the
            reading: a deviation in the same direction pushes the needle higher (possible &rarr; likely &rarr;
            highly likely). The gauge label names the condition and likelihood; the markers driving it appear
            beneath it.
          </div>
        </div>
      </Popover.Content>
    </Popover>
  );

  renderArrow = (direction: 'up' | 'down', label: string) => {
    const src = direction === 'up' ? arrow_up : arrow_down;
    const alt = direction === 'up' ? '↑' : '↓';
    return (
      <span style={{ whiteSpace: 'nowrap' }}>
        <img src={src} alt={alt} style={arrowStyle} />
        {label}
      </span>
    );
  };

  // Markers driving the read, shown as a caption beneath the gauge with the
  // triangle to the left of each name (the "Logic" format authored in the DB
  // Insight tab). The condition + likelihood live in the gauge label, so this is
  // just the supporting markers; nothing renders in the balanced state.
  renderMarkers = (gauge: AdrenalGauge) => {
    if (gauge.markers.length === 0) return null;
    return (
      <div style={{ textAlign: 'center', color: '#000', fontSize: '13.5px', marginTop: '4px' }}>
        {gauge.markers.map((m, index) => (
          <React.Fragment key={`${m.label}-${index}`}>
            {index > 0 && <>,{' '}</>}
            {this.renderArrow(m.direction, m.label)}
          </React.Fragment>
        ))}
      </div>
    );
  };

  render() {
    const { bloodworkAnalysis: ba } = this.props;
    if (!ba.bloodwork) return null;

    // The Na/K ratio is the primary marker and sets the direction; with no ratio
    // there's nothing to anchor the gauge on, so the panel doesn't render.
    const gauge = adrenalGauge(ba.sodiumPotassiumRatio, {
      cortisolAM: ba.cortisolAM,
      cortisolPM: ba.cortisolPM,
      dheaS: ba.dheaS,
      pregnenolone: ba.pregnenolone,
    });
    if (!gauge) return null;

    return (
      // Standard 300px eval panel — same size as the Glucose / Immune / RBC panels.
      <Card className="evaluationPanel" id="stress-adrenal-regulation-insights">
        <Card.Header className="evaluationPanelHeading">
          Stress &amp; Adrenal Insights
          <OverlayTrigger placement="bottom" overlay={this.gaugeInfoPopover()}>
            <FontAwesomeIcon
              icon="info-circle"
              style={{ cursor: 'pointer', color: '#fff', fontSize: 14, marginLeft: 8, verticalAlign: 'middle' }}
            />
          </OverlayTrigger>
        </Card.Header>
        <Card.Body>
          <div className="anemiaStatusContainer">
            <LabsmartsGuage
              text={`${CONDITION_LABEL[gauge.condition]} is`}
              chartContainerId="stressAdrenalRegulationGuage"
              bloodwork={ba.bloodwork}
              probability={gauge.probability}
              category={gauge.category}
            />
            {this.renderMarkers(gauge)}
          </div>
        </Card.Body>
      </Card>
    );
  }
}
